import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CockpitPage,
  CockpitMasthead,
  HeroMetric,
  HeroDivider,
  CockpitFilterBar,
  CockpitSearch,
  CockpitChipGroup,
  CockpitListShell,
  CockpitColumns,
  CockpitRow,
  CockpitEmpty,
  InlineMeta,
  StatusDot,
} from "@/components/cockpit/CockpitShell";
import { useCustomersServerPaging } from "@/hooks/useCustomersServerPaging";

type SortField = "jobCount" | "totalValue" | "openIssueCount" | "lastActivity";
type SortDir = "asc" | "desc";

const TEMPLATE = "minmax(220px,1.3fr) minmax(150px,.8fr) 90px 90px 120px 110px 24px";
const PAGE_SIZE = 25;

export default function CustomersListCockpitServer() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [activeView, setActiveView] = useState<"all" | "active" | "issues" | "recent" | "completed">("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [branchFilter, setBranchFilter] = useState("all");
  const [regionFilter, setRegionFilter] = useState("all");
  const [sortField, setSortField] = useState<SortField | undefined>();
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(1);

  const data = useCustomersServerPaging({
    enabled: true,
    page,
    pageSize: PAGE_SIZE,
    search,
    view: activeView,
    city: cityFilter,
    branch: branchFilter,
    region: regionFilter,
    sortField,
    sortDir,
  });

  const totalPages = Math.max(1, Math.ceil(data.count / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const summary = data.summary;
  const rows = data.rows;

  const setView = (value: string) => {
    setActiveView(value as typeof activeView);
    setPage(1);
  };

  const setSearchReset = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const exportCsv = () => {
    const headers = ["Name", "Phone", "Email", "Address", "City", "Branch", "Region", "Jobs", "Open Jobs", "Open Issues", "Total Value", "Last Activity"];
    const csv = [headers, ...rows.map((row) => [
      row.name,
      row.phone,
      row.email,
      row.address,
      row.city,
      row.branch,
      row.region,
      row.jobCount,
      row.openJobCount,
      row.openIssueCount,
      row.totalValue,
      row.lastActivity,
    ])].map((record) => record.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "customers.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <CockpitPage>
      <CockpitMasthead
        title="Customers"
        right={(
          <>
            <HeroMetric label="Customers" value={summary?.totalCustomers ?? 0} hero />
            <HeroDivider />
            <HeroMetric label="Open jobs" value={summary?.totalOpenJobs ?? 0} />
            <HeroMetric label="Open issues" value={summary?.totalIssues ?? 0} tone={(summary?.totalIssues ?? 0) > 0 ? "bad" : "default"} />
            <HeroMetric label="Total value" value={`$${Math.round(summary?.totalValue ?? 0).toLocaleString()}`} />
          </>
        )}
      />

      <CockpitFilterBar
        search={<CockpitSearch value={search} onChange={setSearchReset} placeholder="Search customers" />}
        chips={(
          <CockpitChipGroup
            value={activeView}
            onChange={setView}
            options={[
              { value: "all", label: "All", count: summary?.totalCustomers ?? 0 },
              { value: "active", label: "Active", count: summary?.withOpenJobs ?? 0 },
              { value: "issues", label: "Issues", count: summary?.withIssues ?? 0 },
              { value: "recent", label: "Recent", count: summary?.recentActivity ?? 0 },
              { value: "completed", label: "Closed", count: summary?.completedOnly ?? 0 },
            ]}
          />
        )}
        right={(
          <>
            <Select value={cityFilter} onValueChange={(value) => { setCityFilter(value); setPage(1); }}>
              <SelectTrigger className="h-9 text-xs w-[150px] bg-transparent border-border/60">
                <SelectValue placeholder="City" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {(summary?.cities ?? []).map((city) => <SelectItem key={city} value={city}>{city}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={branchFilter} onValueChange={(value) => { setBranchFilter(value); setPage(1); }}>
              <SelectTrigger className="h-9 text-xs w-[150px] bg-transparent border-border/60">
                <SelectValue placeholder="Branch" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {(summary?.branches ?? []).map((branch) => <SelectItem key={branch} value={branch}>{branch}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={regionFilter} onValueChange={(value) => { setRegionFilter(value); setPage(1); }}>
              <SelectTrigger className="h-9 text-xs w-[150px] bg-transparent border-border/60">
                <SelectValue placeholder="Region" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                {(summary?.regions ?? []).map((region) => <SelectItem key={region} value={region}>{region}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="h-9 text-xs" onClick={exportCsv}>
              <Download className="h-3.5 w-3.5 mr-1.5" /> Export
            </Button>
          </>
        )}
      />

      <CockpitListShell>
        <CockpitColumns template={TEMPLATE}>
          <span>Customer</span>
          <span>Contact</span>
          <button className="text-left" onClick={() => { setSortField("jobCount"); setSortDir((prev) => sortField === "jobCount" && prev === "desc" ? "asc" : "desc"); }}>Jobs</button>
          <button className="text-left" onClick={() => { setSortField("openIssueCount"); setSortDir((prev) => sortField === "openIssueCount" && prev === "desc" ? "asc" : "desc"); }}>Issues</button>
          <button className="text-left" onClick={() => { setSortField("totalValue"); setSortDir((prev) => sortField === "totalValue" && prev === "desc" ? "asc" : "desc"); }}>Value</button>
          <button className="text-left" onClick={() => { setSortField("lastActivity"); setSortDir((prev) => sortField === "lastActivity" && prev === "desc" ? "asc" : "desc"); }}>Updated</button>
          <span />
        </CockpitColumns>

        {data.loading && !summary && rows.length === 0 ? (
          <CockpitEmpty message="Loading customers…" />
        ) : data.error && !summary && rows.length === 0 ? (
          <CockpitEmpty message="Unable to load customers." />
        ) : rows.length === 0 ? (
          <button type="button" onClick={() => navigate("/opportunities")} className="block w-full text-left">
            <CockpitEmpty message="No customers match this view." />
          </button>
        ) : rows.map((customer) => (
          <CockpitRow
            key={customer.customerId || customer.name}
            template={TEMPLATE}
            to={customer.customerId ? `/customers/${customer.customerId}` : `/customers/${encodeURIComponent(customer.name)}`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <StatusDot tone={customer.openIssueCount > 0 ? "bad" : customer.openJobCount > 0 ? "good" : "muted"} />
                <p className="text-sm font-medium text-foreground truncate">{customer.name}</p>
              </div>
              <InlineMeta items={[customer.address || customer.city || "Florida"]} />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-foreground tabular-nums truncate">{customer.phone}</p>
              <p className="text-xs text-muted-foreground truncate">{customer.email || "No email"}</p>
            </div>
            <p className="text-sm text-foreground tabular-nums">{customer.jobCount} <span className="text-xs text-muted-foreground">total</span></p>
            <p className="text-sm text-foreground tabular-nums">{customer.openIssueCount || "—"}</p>
            <p className="text-sm text-foreground tabular-nums">${customer.totalValue.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground tabular-nums">{customer.lastActivity.slice(0, 10)}</p>
          </CockpitRow>
        ))}
      </CockpitListShell>

      {data.count > 0 && (
        <div className="flex items-center justify-between border-t border-border/60 pt-4">
          <p className="text-xs text-muted-foreground tabular-nums">
            Showing {(safePage - 1) * PAGE_SIZE + 1}-{Math.min(safePage * PAGE_SIZE, data.count)} of {data.count}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>Previous</Button>
            <span className="text-xs text-muted-foreground tabular-nums">{safePage} / {totalPages}</span>
            <Button variant="outline" size="sm" className="h-8 text-xs" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)}>Next</Button>
          </div>
        </div>
      )}
    </CockpitPage>
  );
}
